import styled from '@emotion/styled';
import { Grid, useMediaQuery, useTheme } from '@mui/material';
import { useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'next/navigation';
import BookingCard from './components/booking-card';
import useRouteId from '@/common/hooks/use-route-id';
import { ExperienceMode, ExperienceType, useGetFullExperienceByIdQuery } from '@/generated/graphql';
import { nullToUndefined } from '@/utils/nullToUndefined';
import { FullPageLoader } from '@/components/full-page-loader/full-page-loader';
import { ServerError } from '@/components/errors/server-error/server-error';
import { NotFoundError } from '@/components/errors/not-found-error/not-found-error';
import MobileActionFooter from './components/mobile-action-footer';
import ExperienceIntroComponent from './components/experience-intro';
import HostProfile from './components/host-profile';
import WhatToDo from './components/what-to-do';
import Collapsible from './components/collapsible';
import TabbedResponseSelector from './components/tabbed-response-selector';
import InPerson from './components/in-person';
import Sticky from 'react-stickynode';
import { track } from '@/components/analytics/track';
import { ClientAnalyticsEventType } from '@/components/analytics/constants';

const gradients: Record<string, { Online: string; InPerson: string }> = {
  Event: {
    Online: 'linear-gradient(180deg, #bf9bb9 0%, #FFF3DC 33.33%, #FFF3DC 47.40%, #FFF 68.75%);',
    InPerson: 'linear-gradient(180deg, #1AA16B 0%, #FFF3DC 39.92%, #FFF3DC 57.76%, #FFF 80.59%)',
  },
  Box: {
    Online: 'linear-gradient(180deg, #F8D16F 0%, #FDEDC8 33.95%, #FFF3DC 41.00%, #FFF 58.46%)',
    InPerson: 'linear-gradient(180deg, #F8D16F 0%, #FDEDC8 33.95%, #FFF3DC 41.00%, #FFF 58.46%)',
  },
  Video: {
    Online: 'linear-gradient(180deg, #F8D16F 0%, #FDEDC8 33.95%, #FFF3DC 41.00%, #FFF 58.46%)',
    InPerson: 'linear-gradient(180deg, #F8D16F 0%, #FDEDC8 33.95%, #FFF3DC 41.00%, #FFF 58.46%)',
  },
  Schedule: {
    Online: 'linear-gradient(180deg, #bf9bb9 0%, #FFF3DC 33.33%, #FFF3DC 47.40%, #FFF 68.75%);',
    InPerson: 'linear-gradient(180deg, #1AA16B 0%, #FFF3DC 39.92%, #FFF3DC 57.76%, #FFF 80.59%)',
  },
};
const Container = styled.div<{ gradient: string }>`
  height: auto;
  margin: 0;
  padding: 0 12.5% 90px 12.5%;
  width: 100%;
  min-height: 200vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;
  background: ${props => props.gradient};
  @media (max-width: 768px) {
    padding: 0;
  }
`;
const MainSection = styled(Grid)`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: flex-start;
  padding-top: 90px;
  margin-bottom: 50px;
  @media (max-width: 768px) {
    height: auto;
    padding-top: unset;
  }
`;
const BookingCardWrapper = styled(Grid)`
  height: 100%;
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  align-items: center;
  margin-bottom: 16px;
  @media (max-width: 768px) {
    flex-direction: column;
    width: 100%;
    align-items: flex-start;
    justify-content: flex-start;
    height: auto;
  }
`;

const StickyInner = styled.div`
  width: 100%;
  height: 100%;
  padding-bottom: 32px;
  padding-left: 32px;
`;
const IntroDiv = styled(Grid)`
  height: 100%;
  background-color: transparent;
  margin: 0;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  overflow: hidden;
  & a {
    cursor: pointer;
  }
  order: 0;
  @media (max-width: 768px) {
    height: auto;
    padding: 0;
    display: block;
    max-height: unset;
    order: 1;
  }
`;
const getGradient = (
  type: ExperienceType = ExperienceType.Event,
  mode: ExperienceMode = ExperienceMode.Online,
): string => {
  return gradients[type][mode];
};
// const StyledImage = styled(Image)`
//   border-radius: 28px;
//   height: auto;
// `;
export function NewExperiencePage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const experienceId = useRouteId();
  const searchParams = useSearchParams();
  const { data, loading, error } = useGetFullExperienceByIdQuery({
    variables: {
      id: experienceId,
    },
    skip: !experienceId,
  });

  const experience = nullToUndefined(data?.experience);
  const mode = experience?.mode;

  const title = experience?.title;
  const trackedExperienceRef = useRef<string | null>(null);

  const utmParams = useMemo(() => {
    const params: Record<string, string> = {};
    if (!searchParams) {
      return params;
    }

    const utmKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];

    utmKeys.forEach(key => {
      const value = searchParams.get(key);
      if (value) {
        params[key] = value;
      }
    });

    return params;
  }, [searchParams]);

  useEffect(() => {
    if (!experience?.id || typeof window === 'undefined') {
      return;
    }

    const utmKey = Object.keys(utmParams)
      .sort()
      .map(key => `${key}:${utmParams[key]}`)
      .join('|');

    const trackingKey = `${experience.id}-${utmKey}`;

    if (trackedExperienceRef.current === trackingKey) {
      return;
    }

    trackedExperienceRef.current = trackingKey;

    const properties: Record<string, string> = {
      experience_id: experience.id,
      experience_type: experience.type || '',
      experience_mode: experience.mode || '',
      experience_title: title || '',
      url: window.location.href,
      path: window.location.pathname,
      ...utmParams,
    };

    Object.keys(properties).forEach(key => {
      if (properties[key] === undefined || properties[key] === null) {
        delete properties[key];
      } else {
        properties[key] = String(properties[key]);
      }
    });

    track(ClientAnalyticsEventType.VIEWED_EXPERIENCE, properties);
  }, [experience?.id, experience?.mode, experience?.type, title, utmParams]);

  if (loading || !experienceId) {
    return <FullPageLoader />;
  }

  if (!loading && error) {
    return <ServerError />;
  }

  if (!loading && !experience) {
    return <NotFoundError />;
  }
  if (!experience) {
    return <NotFoundError />;
  }

  return (
    <Container gradient={getGradient(experience.type, experience.mode)}>
      <MainSection container>
        <IntroDiv item lg={6}>
          <ExperienceIntroComponent
            title={title!}
            type={experience.type!}
            isOnline={experience.mode === ExperienceMode.Online}
            description={experience?.content?.description || 'No description provided'}
          />
          <HostProfile experience={experience} />
          <WhatToDo text={experience?.content?.what || 'No description provided'} />
          {mode &&
            mode === ExperienceMode.InPerson &&
            experience?.approximateLocation &&
            experience.type !== ExperienceType.Box && <InPerson experience={experience} />}
        </IntroDiv>
        {isMobile && (
          <BookingCardWrapper item lg={6}>
            <BookingCard experience={experience} />
          </BookingCardWrapper>
        )}
        {isMobile && <MobileActionFooter experience={experience} />}
        {!isMobile && (
          <Grid item lg={6}>
            <Sticky top={100} bottomBoundary={`#a`}>
              <StickyInner>
                <BookingCard experience={experience} />
              </StickyInner>
            </Sticky>
          </Grid>
        )}
      </MainSection>
      <div style={{ display: isMobile ? 'none' : 'block', marginBottom: '32.5px' }} id="a"></div>
      {isMobile ? (
        <Collapsible content={experience?.content || {}} />
      ) : (
        <TabbedResponseSelector content={experience?.content || {}} />
      )}
    </Container>
  );
}
